import {
  IQuestionnaireSpecifiable,
  IQuestionnaire,
  QuestionnaireType,
  IQuestionnaireExtension,
  IQuestionnaireExpression,
  instanceOfIQuestionnaireExpression,
  instanceOfIQuestionnaireExtension,
  IQuestionnaireCodeableConcept,
  IQuestionnaireCoding,
  instanceOfIQuestionnaireCoding,
  instanceOfIQuestionnaireCodeableConcept,
} from "../../../../../../../documaker/domain/format/objects";
import { IXElementContentsObjectBean, IXElementContentsObjectRelationalBean, IXElementContentsObjectRelationalItemBean } from "../../../beans";
import { IntegerTranslator } from "../../values";
import { RelationTranslator } from "./RelationTranslator";

abstract class AbstractQuestionnaireTranslator {
  protected jsonParseAs<T>(text: string, defaultValue: T): T {
    try {
      return JSON.parse(text);
    } catch (e) {
      return defaultValue;
    }
  }

  protected toRelationalItemBeanKeyValue(key: string, value: string): IXElementContentsObjectRelationalItemBean | undefined {
    return value
      ? {
        "@key": key,
        "@value": value,
      }
      : undefined;
  }
}

export class QuestionnaireTranslator extends AbstractQuestionnaireTranslator {
  from(bean: IXElementContentsObjectBean): IQuestionnaire {
    return this.relationalItems.from(
      bean,
      is => {
        const _guidItem = is.find(i => i["@key"] === "__guid");
        const typeItem = is.find(i => i["@key"] === "type");
        const linkIdItem = is.find(i => i["@key"] === "linkId");
        const textItem = is.find(i => i["@key"] === "text");
        const repeatsItem = is.find(i => i["@key"] === "repeats");
        const maxOccursItem = is.find(i => i["@key"] === "maxOccurs");
        const codeItem = is.find(i => i["@key"] === "code");
        const answerValueSetItem = is.find(i => i["@key"] === "answerValueSet");
        const extensionItem = is.find(i => i["@key"] === "extension");
        return {
          _guid: _guidItem ? _guidItem["@value"] : undefined,
          _childGuids: this.childrenRelationalItems.from(bean, items => items.map(i => i["@key"]), []),
          type: (typeItem ? typeItem["@value"] : undefined) as QuestionnaireType | undefined,
          linkId: linkIdItem ? linkIdItem["@value"] : undefined,
          text: textItem ? textItem["@value"] : undefined,
          repeats: repeatsItem ? repeatsItem["@value"].toLowerCase() === "true" : undefined,
          maxOccurs: maxOccursItem ? this.integer.from(maxOccursItem["@value"]) : undefined,
          fhirPath: this.fhirPathExtension.from(bean),
          code: codeItem ? codeItem["@value"] : undefined,
          gtable: this.gtableExtension.from(bean),
          answerValueSet: answerValueSetItem ? answerValueSetItem["@value"] : undefined,
          extension: extensionItem ? this.jsonParseAs(extensionItem["@value"], undefined) : undefined,
        };
      },
      {
        _guid: "",
        _childGuids: [],
        type: undefined as QuestionnaireType | undefined,
        linkId: "",
        text: "",
        repeats: undefined,
        maxOccurs: undefined,
        fhirPath: undefined,
        code: undefined,
        gtable: undefined,
        answerValueSet: undefined,
        extension: undefined,
      });
  }

  to(object: IQuestionnaireSpecifiable): IXElementContentsObjectRelationalBean {
    // チェックの場合linkId未指定でもcode許容されてれば記録する.
    const maxOccurs = this.integer.to(object.questionnaire.maxOccurs);
    return this.relationalItems.to(object.questionnaire.linkId || object.questionnaire.code
      ? [
        object.questionnaire._guid
          ? {
            "@key": "__guid",
            "@value": object.questionnaire._guid,
          }
          : undefined,
        object.questionnaire.type
          ? {
            "@key": "type",
            "@value": object.questionnaire.type,
          }
          : undefined,
        object.questionnaire.linkId
          ? {
            "@key": "linkId",
            "@value": object.questionnaire.linkId,
          }
          : undefined,
        object.questionnaire.text
          ? {
            "@key": "text",
            "@value": object.questionnaire.text,
          }
          : undefined,
        object.questionnaire.repeats
          ? {
            "@key": "repeats",
            "@value": "true",
          }
          : undefined,
        maxOccurs
          ? {
            "@key": "maxOccurs",
            "@value": maxOccurs,
          }
          : undefined,
        object.questionnaire.code
          ? {
            "@key": "code",
            "@value": object.questionnaire.code,
          }
          : undefined,
        object.questionnaire.answerValueSet
          ? {
            "@key": "answerValueSet",
            "@value": object.questionnaire.answerValueSet,
          }
          : undefined,
        this.toRelationalItemBeanKeyValue("extension", object.questionnaire.extension && object.questionnaire.extension.length > 0 ? JSON.stringify(object.questionnaire.extension) : undefined),
      ].filter(i => !!i).map(i => i as IXElementContentsObjectRelationalItemBean).concat(this.fhirPathExtension.to(object.questionnaire.fhirPath)).concat(this.gtableExtension.to(object.questionnaire.gtable))
      : []);
  }

  toChildren(object: IQuestionnaireSpecifiable): IXElementContentsObjectRelationalBean {
    return this.childrenRelationalItems.to(object.questionnaire._childGuids
      ? object.questionnaire._childGuids.map(i => ({
        "@key": i,
      }))
      : []);
  }

  private readonly relationalItems = new RelationTranslator("questionnaire");
  private readonly fhirPathExtension = new QuestionnaireExtensionTranslator("fhirPath");
  private readonly gtableExtension = new QuestionnaireExtensionTranslator("gtable");
  private readonly childrenRelationalItems = new RelationTranslator("questionnaire-children");
  private readonly integer = new IntegerTranslator();
}

export class QuestionnaireExtensionTranslator {
  constructor(private readonly _prefix: string) {
    this.valueExpression = new QuestionnaireExpressionTranslator(this._prefix + ".valueExpression");
    this.valueCodeableConcept = new QuestionnaireCodeableConceptTranslator(this._prefix + ".valueCodeableConcept");
  }

  from(bean: IXElementContentsObjectBean): IQuestionnaireExtension {
    return this.relationalItems.from(
      bean,
      is => {
        const urlItem = is.find(i => i["@key"] === this._prefix + ".url");
        return urlItem
          ? {
            url: urlItem["@value"],
            valueExpression: this.valueExpression.from(bean),
            valueCodeableConcept: this.valueCodeableConcept.from(bean),
          }
          : undefined;
      },
      undefined
    );
  }

  to(object: IQuestionnaireExtension): IXElementContentsObjectRelationalItemBean[] {
    return instanceOfIQuestionnaireExtension(object)
      ? [
        {
          "@key": this._prefix + ".url",
          "@value": object.url,
        },
      ].map(i => i as IXElementContentsObjectRelationalItemBean).concat(this.valueExpression.to(object.valueExpression)).concat(this.valueCodeableConcept.to(object.valueCodeableConcept))
      : [];
  }

  private readonly relationalItems = new RelationTranslator("questionnaire");
  private readonly valueExpression: QuestionnaireExpressionTranslator;
  private readonly valueCodeableConcept: QuestionnaireCodeableConceptTranslator;
}

export class QuestionnaireExpressionTranslator {
  constructor(private readonly _prefix: string) {}

  from(bean: IXElementContentsObjectBean): IQuestionnaireExpression {
    return this.relationalItems.from(
      bean,
      is => {
        const nameItem = is.find(i => i["@key"] === this._prefix + ".name");
        const languageItem = is.find(i => i["@key"] === this._prefix + ".language");
        const expressionItem = is.find(i => i["@key"] === this._prefix + ".expression");
        return expressionItem
          ? {
            name: nameItem ? nameItem["@value"] : undefined,
            language: languageItem ? languageItem["@value"] : undefined,
            expression: expressionItem["@value"],
          }
          : undefined;
      },
      undefined
    );
  }

  to(object: IQuestionnaireExpression): IXElementContentsObjectRelationalItemBean[] {
    return instanceOfIQuestionnaireExpression(object)
      ? [
        object.name
        ? {
          "@key": this._prefix + ".name",
          "@value": object.name,
        }
        : undefined,
        object.language
        ? {
          "@key": this._prefix + ".language",
          "@value": object.language,
        }
        : undefined,
        {
          "@key": this._prefix + ".expression",
          "@value": object.expression,
        },
      ].filter(i => !!i)
      : [];
  }

  private readonly relationalItems = new RelationTranslator("questionnaire");
}

export class QuestionnaireCodeableConceptTranslator {
  constructor(private readonly _prefix: string) {
    this.coding = new QuestionnaireCodingsTranslator(_prefix + ".coding");
  }

  from(bean: IXElementContentsObjectBean): IQuestionnaireCodeableConcept {
    return this.relationalItems.from(
      bean,
      is => {
        const coding = this.coding.from(bean);
        const textItem = is.find(i => i["@key"] === this._prefix + ".text");
        return coding
          ? {
            coding: coding,
            text: textItem ? textItem["@value"] : undefined,
          }
          : undefined;
      },
      undefined
    );
  }

  to(object: IQuestionnaireCodeableConcept): IXElementContentsObjectRelationalItemBean[] {
    return instanceOfIQuestionnaireCodeableConcept(object)
      ? [
        object.text
        ? {
          "@key": this._prefix + ".text",
          "@value": object.text,
        }
        : undefined,
      ].filter(i => !!i).map(i => i as IXElementContentsObjectRelationalItemBean).concat(this.coding.to(object.coding))
      : [];
  }

  private readonly relationalItems = new RelationTranslator("questionnaire");
  private readonly coding: QuestionnaireCodingsTranslator;
}

export abstract class AbstractQuestionnaireArrayTranslator<T> {
  constructor(protected readonly _prefix: string) {}

  protected abstract createTranslator(prefix: string): IQuestionnaireRelationalTranslatable<T>;

  from(source: IXElementContentsObjectBean): T[] {
    return this.relationalItems.from(
      source,
      is => this.extractPrefixes(is).map(p => this.createTranslator(p).from(source)),
      undefined
    );
  }

  private extractPrefixes(source: IXElementContentsObjectRelationalItemBean[]): string[] {
    return source
      ? source.map(i => this.extractPrefix(i)).filter(p => !!p).sort()
      : [];
  }

  private extractPrefix(source: IXElementContentsObjectRelationalItemBean): string | undefined {
    const matched = source["@key"] ? source["@key"].match(new RegExp("^(?<prefix>" + this._prefix + "\[[0-9]\]+)")) : null;
    return matched ? matched.groups.prefix : undefined;
  }

  to(source: T[]): IXElementContentsObjectRelationalItemBean[] {
    return Array.isArray(source) && source.length > 0
      ? source.map((s, i) => this.createTranslator(this._prefix + "[" + i + "]").to(s)).reduce((a, b) => a.concat(b))
      : [];
  }

  private readonly relationalItems = new RelationTranslator("questionnaire");
}

export interface IQuestionnaireRelationalTranslatable<TInstance> {
  from(source: IXElementContentsObjectBean): TInstance;
  to(object: TInstance): IXElementContentsObjectRelationalItemBean[];
}

export class QuestionnaireCodingsTranslator extends AbstractQuestionnaireArrayTranslator<IQuestionnaireCoding> {
  constructor(prefix: string) {
    super(prefix);
  }

  protected createTranslator(prefix: string): IQuestionnaireRelationalTranslatable<IQuestionnaireCoding> {
    return new QuestionnaireCodingTranslator(prefix);
  }
}

export class QuestionnaireCodingTranslator implements IQuestionnaireRelationalTranslatable<IQuestionnaireCoding> {
  constructor(private readonly _prefix: string) {}

  from(bean: IXElementContentsObjectBean): IQuestionnaireCoding {
    return this.relationalItems.from(
      bean,
      is => {
        const systemItem = is.find(i => i["@key"] === this._prefix + ".system");
        const codeItem = is.find(i => i["@key"] === this._prefix + ".code");
        const displayItem = is.find(i => i["@key"] === this._prefix + ".display");
        return codeItem
          ? {
            system: systemItem ? systemItem["@value"] : undefined,
            code: codeItem ? codeItem["@value"] : undefined,
            display: displayItem ? displayItem["@value"] : undefined,
          }
          : undefined;
      },
      undefined
    );
  }

  to(object: IQuestionnaireCoding): IXElementContentsObjectRelationalItemBean[] {
    return instanceOfIQuestionnaireCoding(object)
      ? [
        object.system
        ? {
          "@key": this._prefix + ".system",
          "@value": object.system,
        }
        : undefined,
        object.code
        ? {
          "@key": this._prefix + ".code",
          "@value": object.code,
        }
        : undefined,
        object.display
        ? {
          "@key": this._prefix + ".display",
          "@value": object.display,
        }
        : undefined,
      ].filter(i => !!i)
      : [];
  }

  private readonly relationalItems = new RelationTranslator("questionnaire");
}
